/**
 * Disable Zod 4.x's JIT validator compiler — Safari can't parse the
 * `new Function(...args, body)` it generates ("Unexpected token '{'.
 * Expected ')' to end a compound expression.", no source path because
 * the body is never on disk).
 *
 * SECONDARY guard. The PRIMARY guard is the inline `<script>` in each app's
 * index.html that sets `globalThis.__zod_globalConfig = { jitless: true }`
 * before the module bundle loads — that is bundle-order-PROOF (it runs before
 * any module, so jitless is set before any schema is constructed). This file's
 * import-first ordering trick only holds if the bundler keeps this side effect
 * ahead of every schema's construction; it currently does, but a refactor could
 * silently break it, and CI never runs Safari to catch the regression. Keep both:
 * the inline script is the real guarantee, this is defense-in-depth + the place
 * the rationale lives. zod's core.js does `globalThis.__zod_globalConfig ??= {}`
 * then `Object.assign`s, so the inline pre-set and this `z.config()` agree.
 *
 * MUST run before any module imports a Zod schema. Implemented as a
 * standalone file so importing it as the first import in each app's
 * main.tsx fires its side effect ahead of every transitive zod-using
 * import. If this code lived in main.tsx itself, ES module hoisting
 * would run all `import` statements before this `z.config(...)` call,
 * defeating the ordering — schemas would already be JIT-compiled
 * by the time the flag flipped.
 *
 * Known consumers (every app entry that bundles @frontend/api/* imports
 * this FIRST, AND ships the inline guard in its index.html):
 *   - frontend/src/main.tsx           (Tricorder digital-twin app)
 *   - apps/care-planner/src/main.tsx  (Care Planner)
 *   - apps/compatibility/src/main.tsx (Y-Site Compatibility)
 *   - apps/knowledge-profile/src/main.tsx        (Knowledge Profile)
 *   - apps/roster/src/main.tsx        (Shift Planner)
 *
 * Adding a new app under apps/ that aliases @frontend? Add the inline
 * `<script>globalThis.__zod_globalConfig = { jitless: true }</script>` to its
 * index.html (the real guarantee), add it here, and import this module first in
 * its main.tsx — or Safari users hit "Unexpected token '{'" at the first
 * schema parse(). The checks.yml app-checks job asserts both the bundle flag
 * and the inline script landed.
 *
 * Trade-off: validation is slightly slower at runtime via the
 * interpreter path. The schemas in this app are tiny (auth, sessions,
 * messages) — the JIT speedup wasn't measurable.
 */
import { z } from 'zod'

z.config({ jitless: true })
